import React, { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useSetRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';
import RadioButton from 'up-ops-web-shared-components/lib/components/radio/radio-button';
import StyledDiv from 'up-ops-web-shared-components/lib/components/styled-div';

import FormHeader from 'components/FormHeader';
import FileDownloadLink from 'components/FileDownloadLink';
import FormFileUpload from 'components/FormFileUpload';
import { FormRow, UploadHint, UploadHintList } from 'styles/CommonStyles';
import FormTextInput from 'components/FormTextInput';
import { FullWidthTextArea } from 'components/FormTextArea';
import { TwoAnswerRadioGroup } from 'components/FormRadioGroup';
import FormSubmit from 'components/FormSubmit';
import FormTracker from 'components/FormTracker';
import useSubmitActions from 'hooks/submit';
import useUploadActions from 'hooks/upload';
import { acceptedFiles, FormIdentifier } from 'lib/constants';
import { normalizePhoneNumber } from 'lib/utils';
import { patterns } from 'lib/validation';
import { showSubmitSuccessState } from 'state/atoms/submit';

const IdentityTheftForm = () => {
  const { i18n, t } = useTranslation();
  const formMethods = useForm();
  const { submitForm } = useSubmitActions();
  const { uploadFiles } = useUploadActions();
  const setShowSuccess = useSetRecoilState(showSubmitSuccessState);

  const onSubmit = useCallback(
    (data: any) => {
      const { documents, phoneNumber, ...otherData } = data;
      const formData = {
        formType: FormIdentifier.IDENTITY_THEFT,
        phoneNumber: normalizePhoneNumber(phoneNumber),
        ...otherData,
      };
      const formSessionUuid = uuidv4();

      return uploadFiles(formSessionUuid, 'fraud', documents)
        .then((uploadedDocs) =>
          submitForm(
            formSessionUuid,
            { ...formData, documents: uploadedDocs },
            i18n.language
          )
        )
        .then(() => setShowSuccess(true));
    },
    [i18n.language, setShowSuccess, submitForm, uploadFiles]
  );

  return (
    <FormProvider {...formMethods}>
      <FormHeader
        description={t('forms.identityTheft.description')}
        title={t('forms.identityTheft.name')}
      />
      <StyledDiv marginVertical="m">
        <FileDownloadLink
          href="/downloads/Identity Theft Affidavit.pdf"
          text={t('forms.identityTheft.downloadAffidavit')}
        />
      </StyledDiv>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <FormRow>
          <FormTextInput
            label={t<string>('labels.name')}
            name="name"
            rules={{
              pattern: {
                value: patterns.noSpecialCharacters,
                message: t<string>('validation.invalid'),
              },
              required: t<string>('validation.required'),
            }}
          />
          <FormTextInput
            label={t<string>('labels.emailAddress')}
            name="email"
            rules={{
              pattern: {
                message: t<string>('validation.emailFormat'),
                value: patterns.emailAddress,
              },
              required: t<string>('validation.required'),
            }}
          />
        </FormRow>
        <FormRow>
          <FormTextInput
            label={t<string>('labels.phoneNumber')}
            name="phoneNumber"
            rules={{
              pattern: {
                message: t<string>('validation.phoneNumberFormat'),
                value: patterns.phoneNumber,
              },
              required: t<string>('validation.required'),
            }}
          />
          <FormTextInput
            label={t<string>('labels.loanIdOrAccountNumberOptional')}
            name="loanIdOrAccountId"
            rules={{
              pattern: {
                message: t<string>('validation.loanIdOrAccountNumberFormat'),
                value: patterns.loanIdOrAccountId,
              },
            }}
          />
        </FormRow>
        <FormRow>
          <TwoAnswerRadioGroup
            name="policeReportFiled"
            rules={{
              required: t<string>('validation.policeReportFilingRequired'),
            }}
            title={t('labels.policeReportFiling')}
          >
            <RadioButton label={t<string>('common.yes')} value="yes" />
            <RadioButton label={t<string>('common.no')} value="no" />
          </TwoAnswerRadioGroup>
        </FormRow>
        <FormRow>
          <div>
            <FormFileUpload
              acceptTypes={acceptedFiles}
              ariaLabel={t('labels.uploadDocuments')}
              label={
                <>
                  {t('labels.uploadDocuments')}
                  <UploadHint>
                    <Trans i18nKey="labels.uploadFollowingDocuments" />
                    <UploadHintList>
                      <li>{t('labels.idTheftAffidavit')}</li>
                      <li>{t('labels.policeReport')}</li>
                    </UploadHintList>
                  </UploadHint>
                </>
              }
              maxFiles={2}
              name="documents"
              rules={{
                required: t<string>('validation.uploadDocumentsRequired'),
              }}
            />
          </div>
        </FormRow>
        <FormRow>
          <FullWidthTextArea
            label={t<string>('labels.comments')}
            name="comments"
          />
        </FormRow>
        <FormSubmit formType={FormIdentifier.IDENTITY_THEFT} />
        <FormTracker formType={FormIdentifier.IDENTITY_THEFT} />
      </form>
    </FormProvider>
  );
};

export default IdentityTheftForm;
